import {
    averageIncludedMultiplier,
    resolveModuleMultiplier,
} from './preContractPpwPricing';
import { loadPreContractFullModuleLayouts } from './preContractFullModuleLayouts';
import {
    isPreContractGreyPanel,
    isPreContractPurplePanel,
} from './preContractModuleLayout';
import type { PreContractModuleCell, PreContractRoofModuleLayout } from './preContractTypes';

export type SelectedModuleRow = {
    id: string;
    roofLabel: string;
    multiplier: number | null;
    solarAccess: number | null;
    productionKwh: number | null;
};

export type SystemModuleRow = SelectedModuleRow & {
    included: boolean;
    shadeTier: 'grey' | 'purple' | 'normal';
};

export type CollectAllSystemModulesOptions = {
    /** Max-fill baseline — merged in so trimmed live layouts still list every panel. */
    baselineLayouts?: PreContractRoofModuleLayout[];
    /** When true (default), grey/purple panels stay in the catalog even if toggles are off. */
    includeGreyPanels?: boolean;
    includePurplePanels?: boolean;
};

function moduleCellKey(cell: { id?: string; meshId?: string }): string {
    const id = String(cell.id ?? '').trim().toUpperCase();
    if (id) return id;
    const meshId = String(cell.meshId ?? '').trim().toUpperCase();
    return meshId;
}

function mergeModuleCell(
    base: NonNullable<PreContractModuleCell>,
    overlay: NonNullable<PreContractModuleCell>,
): NonNullable<PreContractModuleCell> {
    return {
        ...base,
        ...overlay,
        included: overlay.included,
        solarAccess: overlay.solarAccess > 0 ? overlay.solarAccess : base.solarAccess,
        productionKwh:
            typeof overlay.productionKwh === 'number' && overlay.productionKwh > 0
                ? overlay.productionKwh
                : base.productionKwh,
        multiplier:
            typeof overlay.multiplier === 'number' && overlay.multiplier > 0
                ? overlay.multiplier
                : base.multiplier,
        shadeColor: overlay.shadeColor ?? base.shadeColor,
    };
}

function isRoofActiveInSelections(
    layout: PreContractRoofModuleLayout,
    layoutIndex: number,
    roofSelections?: boolean[],
): boolean {
    if (!roofSelections?.length) return true;
    if (roofSelections[layoutIndex] === false) return false;
    if (roofSelections[layout.roofIndex] === false) return false;
    return true;
}

function ingestLayoutCatalog(
    catalog: Map<string, { layout: PreContractRoofModuleLayout; cell: NonNullable<PreContractModuleCell> }>,
    layouts: PreContractRoofModuleLayout[],
    options?: {
        roofSelections?: boolean[];
        /** When false, list every physical module on every roof in layouts. */
        respectRoofSelections?: boolean;
    },
): void {
    const respectRoofSelections = options?.respectRoofSelections !== false;
    const roofSelections = options?.roofSelections;
    layouts.forEach((layout, layoutIndex) => {
        if (respectRoofSelections && !isRoofActiveInSelections(layout, layoutIndex, roofSelections)) {
            return;
        }
        for (const cell of layout.modules) {
            if (!cell) continue;
            const key = moduleCellKey(cell);
            if (!key) continue;
            const existing = catalog.get(key);
            if (!existing) {
                catalog.set(key, { layout, cell });
                continue;
            }
            catalog.set(key, {
                layout,
                cell: mergeModuleCell(existing.cell, cell),
            });
        }
    });
}

function resolveShadeTier(cell: NonNullable<PreContractModuleCell>): SystemModuleRow['shadeTier'] {
    if (isPreContractPurplePanel(cell.solarAccess)) return 'purple';
    if (isPreContractGreyPanel(cell.solarAccess)) return 'grey';
    return 'normal';
}

function sortModulesByProduction<T extends SelectedModuleRow>(rows: T[]): T[] {
    return rows.sort((a, b) => {
        const ap = a.productionKwh ?? -1;
        const bp = b.productionKwh ?? -1;
        if (bp !== ap) return bp - ap;
        const am = a.multiplier ?? -1;
        const bm = b.multiplier ?? -1;
        if (bm !== am) return bm - am;
        return a.id.localeCompare(b.id, undefined, { numeric: true });
    });
}

export type ModuleProductionRank = {
    rank: number;
    total: number;
};

/** Rank every physical module by annual production (1 = highest). */
export function buildModuleProductionRankMap(
    layouts: PreContractRoofModuleLayout[],
): Map<string, ModuleProductionRank> {
    const rows: SelectedModuleRow[] = [];
    layouts.forEach((layout) => {
        for (const cell of layout.modules) {
            if (!cell) continue;
            rows.push(buildSelectedModuleRow(layout, cell));
        }
    });

    const withProduction = rows.filter((row) => row.productionKwh != null && row.productionKwh > 0);
    withProduction.sort((a, b) => {
        const delta = (b.productionKwh ?? 0) - (a.productionKwh ?? 0);
        if (delta !== 0) return delta;
        return a.id.localeCompare(b.id, undefined, { numeric: true });
    });

    const total = withProduction.length;
    const map = new Map<string, ModuleProductionRank>();
    if (total === 0) return map;

    let prevProduction: number | null = null;
    let prevRank = 0;
    withProduction.forEach((row, index) => {
        const production = row.productionKwh ?? 0;
        const rank =
            prevProduction != null && production === prevProduction ? prevRank : index + 1;
        prevProduction = production;
        prevRank = rank;
        map.set(row.id, { rank, total });
    });

    return map;
}

function buildSelectedModuleRow(
    layout: PreContractRoofModuleLayout,
    cell: NonNullable<PreContractRoofModuleLayout['modules'][number]>,
): SelectedModuleRow {
    const sa = Number(cell.solarAccess);
    const production = Number(cell.productionKwh);
    return {
        id: cell.id,
        roofLabel: layout.label,
        multiplier: resolveModuleMultiplier(cell),
        solarAccess: Number.isFinite(sa) && sa > 0 ? sa : null,
        productionKwh: Number.isFinite(production) && production > 0 ? production : null,
    };
}

function selectedModuleRowMap(
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
): Map<string, SelectedModuleRow> {
    const rows = new Map<string, SelectedModuleRow>();
    layouts.forEach((layout, layoutIndex) => {
        if (!isRoofActiveInSelections(layout, layoutIndex, roofSelections)) return;
        for (const cell of layout.modules) {
            if (!cell?.included) continue;
            const key = moduleCellKey(cell);
            if (!key) continue;
            rows.set(key, buildSelectedModuleRow(layout, cell));
        }
    });
    return rows;
}

/** Included modules across active roofs, sorted by production (high → low). */
export function collectSelectedModules(
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
): SelectedModuleRow[] {
    return sortModulesByProduction([...selectedModuleRowMap(layouts, roofSelections).values()]);
}

/** Every physical module on active roofs, sorted by production (high → low). */
export function collectAllSystemModules(
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
    options?: CollectAllSystemModulesOptions,
): SystemModuleRow[] {
    const includeGrey = options?.includeGreyPanels ?? true;
    const includePurple = options?.includePurplePanels ?? true;
    const catalog = new Map<
        string,
        { layout: PreContractRoofModuleLayout; cell: NonNullable<PreContractModuleCell> }
    >();
    const sessionCatalog = loadPreContractFullModuleLayouts();
    if (sessionCatalog?.length) {
        ingestLayoutCatalog(catalog, sessionCatalog, { respectRoofSelections: false });
    }
    if (options?.baselineLayouts?.length) {
        ingestLayoutCatalog(catalog, options.baselineLayouts, { respectRoofSelections: false });
    }
    ingestLayoutCatalog(catalog, layouts, {
        roofSelections,
        respectRoofSelections: false,
    });

    const rows: SystemModuleRow[] = [];
    catalog.forEach(({ layout, cell }) => {
        const tier = resolveShadeTier(cell);
        if (tier === 'grey' && !includeGrey) return;
        if (tier === 'purple' && !includePurple) return;
        rows.push({
            ...buildSelectedModuleRow(layout, {
                ...cell,
                id: cell.id?.trim() || moduleCellKey(cell),
            }),
            included: cell.included,
            shadeTier: tier,
        });
    });
    return sortModulesByProduction(rows);
}

export function summarizeAllSystemModules(
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
    options?: CollectAllSystemModulesOptions,
): {
    count: number;
    selectedCount: number;
    totalProductionKwh: number | null;
    selectedProductionKwh: number | null;
} {
    const rows = collectAllSystemModules(layouts, roofSelections, options);
    let productionSum = 0;
    let productionCount = 0;
    let selectedProductionSum = 0;
    let selectedProductionCount = 0;
    let selectedCount = 0;
    for (const row of rows) {
        if (row.included) selectedCount += 1;
        if (row.productionKwh == null) continue;
        productionSum += row.productionKwh;
        productionCount += 1;
        if (row.included) {
            selectedProductionSum += row.productionKwh;
            selectedProductionCount += 1;
        }
    }
    return {
        count: rows.length,
        selectedCount,
        totalProductionKwh: productionCount > 0 ? Math.round(productionSum) : null,
        selectedProductionKwh:
            selectedProductionCount > 0 ? Math.round(selectedProductionSum) : null,
    };
}

/** Included modules in greedy fill order (matches AI target-fill selection). */
export function collectSelectedModulesInFillOrder(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[] | undefined,
    orderIds: string[],
): SelectedModuleRow[] {
    const rowsById = selectedModuleRowMap(layouts, roofSelections);
    const ordered: SelectedModuleRow[] = [];
    orderIds.forEach((id) => {
        const row = rowsById.get(id);
        if (row) ordered.push(row);
    });
    rowsById.forEach((row, id) => {
        if (!orderIds.includes(id)) ordered.push(row);
    });
    return ordered;
}

export function summarizeSelectedModules(
    layouts: PreContractRoofModuleLayout[],
    roofSelections?: boolean[],
): { count: number; systemMultiplier: number | null; totalProductionKwh: number | null } {
    const rows = collectSelectedModules(layouts, roofSelections);
    const systemMultiplier =
        roofSelections != null
            ? averageIncludedMultiplier(layouts, roofSelections)
            : averageIncludedMultiplier(
                  layouts,
                  layouts.map((layout) => layout.modules.some((c) => c?.included)),
              );
    let productionSum = 0;
    let productionCount = 0;
    for (const row of rows) {
        if (row.productionKwh == null) continue;
        productionSum += row.productionKwh;
        productionCount += 1;
    }
    return {
        count: rows.length,
        systemMultiplier,
        totalProductionKwh:
            productionCount > 0 ? Math.round(productionSum) : null,
    };
}

export function formatModuleProductionKwh(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '—';
    return `${Math.round(value).toLocaleString()} kWh`;
}
